import { DocumentShareToken } from '@app/utils/parseJwt';
import { AdvanceCallback, UpdateQuestionCallback } from '@components/block/Quest/Quest';
import { MessageStep, QuestionStep } from '@components/block/Quest/utils';
import { BasicCompany } from '@dieterApi/company/useCompanyQuery';
import { User, UserQuestionnaire } from '@dieterApi/user/useUserQuery';
import { parseSimpleDate } from '@legalosApi/helpers/parseSimpleDate';
import { getQuestions } from '@legalosApi/hooks/useUpdateSingleQuestion';
import AllowedValueTypes from '@legalosApi/types/AllowedValueTypes';
import { IQuestionnaire } from '@legalosApi/types/IQuestionnaire';
import { ISelectOption } from '@legalosApi/types/ISelectOption';
import QuestionType from '@legalosApi/types/QuestionType';
import { addMonths, differenceInCalendarDays } from 'date-fns';
import { useNavigation } from './useNavigation';

/**
 * Fallback for the server-computed REQUEST_OVERDUE flag: derive it from the
 * received-date answered earlier in the SAME questionnaire (Eingang + 1 Monat,
 * Art. 12 Abs. 3 DSGVO). Needed for the very first run, where the flag the
 * server derived from the date answer may not have reached the client's
 * user-values cache yet. The stored flag (kept fresh server-side, including
 * deadline extensions) always wins when present.
 */
function deriveRequestOverdue(questionnaire: IQuestionnaire): 'Ja' | 'Nein' | undefined {
  const dateQuestion = getQuestions(questionnaire.sections).find(
    (question) => question.tags?.variable === 'REQUEST_TIME_OF_ENTRY'
  );
  const raw = dateQuestion?.value;

  let parsed: { day?: unknown; month?: unknown; year?: unknown } | undefined;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return undefined;
    }
  } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    parsed = raw as { day?: unknown; month?: unknown; year?: unknown };
  }

  if (typeof parsed?.year !== 'number' || typeof parsed?.month !== 'number' || typeof parsed?.day !== 'number') {
    return undefined;
  }

  const deadline = addMonths(new Date(parsed.year, parsed.month, parsed.day), 1);
  return differenceInCalendarDays(deadline, new Date()) < 0 ? 'Ja' : 'Nein';
}

export function useUserValues() {
  const {
    navigation: { expertModeEnabled },
  } = useNavigation();
  return {
    updateFromUserValue(
      user: User | undefined,
      principalCompany: BasicCompany | undefined,
      tokenInfo: DocumentShareToken | undefined,
      step: QuestionStep | MessageStep,
      questionnaire: IQuestionnaire,
      localQuestionnaire: UserQuestionnaire | undefined,
      onUpdateQuestion: UpdateQuestionCallback,
      onAdvance: AdvanceCallback,
      getValue: (variable: string, index?: string) => string | undefined,
      setValue: (value: AllowedValueTypes) => void
    ) {
      const thisStep = step;
      const question = thisStep.question;
      const variable = question.tags.variable;
      const isAuto = question.tags.process.includes('AUTO');
      const isExpertMode = question.tags.process.includes('INFO') && expertModeEnabled;
      const isSemiAuto = question.tags.process.includes('SEMIAUTO');

      // DIRTY HACK FOR DPO SOLUTION
      if (
        question.questionText === 'Soll *Dieter* dein Datenschutzbeauftragter werden?' &&
        (principalCompany?.isDpo || tokenInfo)
      ) {
        console.log('Auto-answering DPO question with "No"');
        const value = question.options.find((o) => o.origLabel.startsWith('Nein'))?.value;
        if (!value) return false;
        value && onUpdateQuestion(question._id, value, true).then(() => onAdvance(step.id));
        return true;
      }
      if (isExpertMode) {
        onUpdateQuestion(question._id, question.options[0].value, true).then(() => onAdvance(step.id));
        return true;
      }

      if (!variable) return false;
      if (!isAuto && !isSemiAuto) return false;

      let index: string | undefined;
      if (question.tags.process.includes('INDEXED_VARIABLE')) {
        // List-entry questionnaires are their own index: they are created without
        // userValueIndex and questionsParseTags falls back to the questionnaire id
        // (see dieter-server). Without the same fallback here, AUTO questions inside
        // an entry questionnaire would read from index 'UNIQUE' and never prefill.
        index = localQuestionnaire?.userValueIndex || localQuestionnaire?.id;
      }

      // Get answer from user values (works for both authenticated and token-based access)
      let answer = getValue(variable, index);

      // REQUEST_OVERDUE is computed by the server from the received-date answered
      // earlier in this very questionnaire - on the first run it may not be in the
      // client cache yet, so derive it locally as a fallback
      if (!answer && variable === 'REQUEST_OVERDUE') {
        answer = deriveRequestOverdue(questionnaire);
      }

      if (!answer) return false;

      switch (question.type) {
        case QuestionType.TEXT:
        case QuestionType.MULTILINE_TEXT:
          isAuto && onUpdateQuestion(question._id, answer, true).then(() => onAdvance(step.id));
          isSemiAuto && setValue(answer);
          break;
        case QuestionType.SELECT_ONE:
          const option = question.options.find((o) => o.label === answer || o.origLabel === answer);
          if (option) {
            isAuto && onUpdateQuestion(question._id, option.value, true).then(() => onAdvance(step.id));
            isSemiAuto && setValue(option.value);
          } else {
            return false;
          }
          break;
        case QuestionType.SELECT_SOME:
          let answers: string[] = [];
          try {
            answers = JSON.parse(answer);
          } catch (error) {
            return false;
          }
          const options = (
            answers
              .map((a) => question.options.find((o) => o.label === a || o.origLabel === a))
              .filter(Boolean) as ISelectOption[]
          ).map((o) => o.value);
          if (options.length) {
            isAuto && onUpdateQuestion(question._id, options, true).then(() => onAdvance(step.id));
            isSemiAuto && setValue(options);
          } else {
            return false;
          }
          break;
        case QuestionType.DATE:
          // User values hold the LegalOS date as a JSON string ('{"day":8,"month":1,"year":2024}').
          // LegalOS and the optimistic cache update expect the object - passing the raw string
          // made the document preview format an Invalid Date and crash the questionnaire.
          const dateValue = parseSimpleDate(answer);

          if (dateValue) {
            isAuto && onUpdateQuestion(question._id, dateValue, true).then(() => onAdvance(step.id));
            isSemiAuto && setValue(dateValue);
          } else {
            return false;
          }
          break;
        default:
          const type = question.type;
          break;
      }
      return isSemiAuto ? false : true;
    },
  };
}
